import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { GitBookProvider } from '@gitbook/embed/react';

import { commitHash, initializeSentry, SentryErrorBoundary } from '@paragon/client-tools';
import { DesignService } from '@paragon/data-connections';
import devTools from '@paragon/dev-tools';
import { initializeDataConnectionsAndLoginTools } from '@paragon/login-tools';
import { SyncProvider } from '@paragon/sync-client';
import { ParagonThemeProvider } from '@paragon/themes';
import { WalkthroughProvider } from '@paragon/tour-guide';

import { registerGlobalClickHandler } from './clickEventHandler';
import { DesignClientDistanceFormattingContext } from './components/controls/DesignClientDistanceFormattingContext';
import RootComponent from './components/RootComponent';
import { getAppMonitor } from './monitor';
import { store, syncClient } from './store';
import { loginAndStartApp } from './store/actions/startAppActions';
import { buildSynchronizationErrorRequestMiddleware } from './store/actions/synchronizationActions';
import { SyncClientManager } from './syncClientManager';
import { chTheme, darkTheme, demoTheme, g2Theme, lightTheme, trussWorksTheme } from './themes';
import { initializeThreeJsStateLayer } from './three/ThreeJsSceneActions';

if (commitHash != null) console.log(`Commit hash: ${commitHash}`);

const appStartupTimerId = getAppMonitor().beginTimedOperation('App Startup');

initializeSentry();
initializeDataConnectionsAndLoginTools(store);
initializeThreeJsStateLayer(store.getState, store.dispatch);
registerGlobalClickHandler(store);

DesignService.applyMiddleware(buildSynchronizationErrorRequestMiddleware(store));

store.dispatch(loginAndStartApp(appStartupTimerId));

const root = createRoot(document.querySelector('.root-anchor')!);
root.render(
  <React.StrictMode>
    <SentryErrorBoundary>
      <SyncProvider value={syncClient}>
        <Provider store={store}>
          <SyncClientManager client={syncClient} />
          <ParagonThemeProvider
            darkTheme={darkTheme}
            lightTheme={lightTheme}
            g2Theme={g2Theme}
            trussWorksTheme={trussWorksTheme}
            demoTheme={demoTheme}
            chTheme={chTheme}
          >
            <GitBookProvider siteURL="https://docs.paragontruss.com">
              <WalkthroughProvider>
                <DesignClientDistanceFormattingContext>
                  <RootComponent />
                </DesignClientDistanceFormattingContext>
              </WalkthroughProvider>
            </GitBookProvider>

            {devTools.renderReduxDevTools()}
          </ParagonThemeProvider>
        </Provider>
      </SyncProvider>
    </SentryErrorBoundary>
  </React.StrictMode>,
);
